import Image from 'next/image';
import { memo, useMemo } from 'react';

import { Link } from '../../../../front/src/components/Link';
import type { Category } from '../../../../front/src/types/api';
import type { HexColor } from '../../../../front/src/utils/colors';
import { hexWithOpacity } from '../../../../front/src/utils/colors';
import { buildApiFileName } from '../../../../front/src/utils/web';

// Rubrique card for the "Le forum des lecteurs" home block (B4_03). Image cover +
// title overlay + clickable category chips — the "façon Psychologies" layout (D-A5).
// Distinct from SubjectBox (the "featured" block card): no tag carousel, dedicated
// forum styling, so the two evolve independently. Chips link to /[tag] on their own
// (like NewHeadingsMenu), no click handler needed.

interface Props {
  title: string;
  tags?: (Category | null)[] | null;
  background?: string | null;
  color?: string | null;
}

export const ReadersForumCard = memo(function ReadersForumCard({
  title,
  tags,
  background,
  color,
}: Props) {
  // Cover image when the rubrique has one; otherwise fall back to a translucent tint
  // of the rubrique colour (same idea as SubjectBox), so a card is never blank.
  const fallbackColor = useMemo(
    () => (color ? hexWithOpacity(color as HexColor, 0.44) : undefined),
    [color],
  );

  return (
    <li
      className="ReadersForumCard relative flex flex-col overflow-hidden rounded-lg min-h-[220px]"
      style={{ backgroundColor: fallbackColor }}
    >
      {background && (
        <Image
          src={buildApiFileName(background)}
          alt=""
          className="object-cover object-center"
          fill
          sizes="(min-width: 768px) 33vw, (min-width: 536px) 50vw, 100vw"
          quality={51}
        />
      )}
      {/* Readability scrim so the title/chips stay legible over any image. */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />

      <div className="relative z-10 mt-auto flex flex-col gap-2 p-4">
        <h3 className="text-[17px] font-bold leading-[1.25] text-white drop-shadow">{title}</h3>
        {!!tags?.length && (
          <div className="flex flex-wrap gap-1.5">
            {tags.map(tag =>
              tag ? (
                <Link key={tag.id || tag.slug} to="category" params={{ tag: tag.slug }}>
                  <a className="truncate rounded-lg bg-white/90 px-2 py-1 text-xs font-bold leading-3 text-gray-900 hover:bg-white">
                    {tag.title || tag.slug}
                  </a>
                </Link>
              ) : null,
            )}
          </div>
        )}
      </div>
    </li>
  );
});
